import type { IdealDesignDto, IdealItemDto } from '@bk/contracts';
import { ownerError } from '@bk/contracts';
import { useState, type ReactNode } from 'react';
import { DoneBand, Feeds, Glance } from './Summary';
import { ItemBase } from './Labels';
import { RoleStandards, standardLine, type OtherRole } from './RoleStandards';
import { RoleWork } from './RoleWork';
import { LibrarySources } from './LibrarySources';

/**
 * L'organigramma ideale di «Organizzazione e ruoli» (ADR 0036, 0067): l'azienda ideale e completa, senza persone, uguale
 * mentre si lavora al passo e nel riepilogo. Un solo albero da chi risponde a chi: gli organi in cima, poi chi guida
 * l'azienda, sotto le aree affiancate con il loro responsabile e ogni ruolo sotto chi ne risponde, anche quelli delle
 * sedi (la sede è un'etichetta sul ruolo); lo stesso ruolo in più sedi è un riquadro solo, con le sedi accanto; lo staff
 * accanto a chi lo ha. Nel riquadro solo ciò che serve a leggerlo: la legenda dice la natura, il dettaglio la fonte. Le
 * funzioni si aprono con lo zoom, ogni sede ha la sua vista con le persone che servono accanto a quelle che ci lavorano,
 * e un incarico sta come etichetta sul ruolo di chi lo esercita (ADR 0101). Sotto il funzionigramma. Toccando un ruolo si vede cosa fa, quante persone servono e
 * perché c'è; in questa fase non si toglie nulla.
 */

const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’`]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** Un ruolo del progetto con il posto in cui sta: l'impresa, una sede, un'attività di una sede. */
interface Slot {
  key: string;
  v: IdealItemDto;
  sede: string | null;
  luogo: string | null;
  att: string | null;
}

const nature = (s: Slot) => s.v.natura ?? 'ruolo';
const where = (s: Slot) => (s.sede ? (s.luogo?.split(',')[0] ?? s.sede) : null);

/** «2-3» da una quantità come «2-3 persone»; null se non è un numero di persone. */
function range(q: string | null): [number, number] | null {
  const m = q?.trim().match(/^(\d+)(?:\s*[-–]\s*(\d+))?(?:\s*person[ae])?$/i);
  if (!m) return null;
  const a = Number(m[1]);
  return [a, m[2] ? Number(m[2]) : a];
}

/** Persone che servono: i posti di lavoro, ognuno con la sua quantità (uno se il progetto non la dice). */
function headcount(slots: Slot[]): [number, number] {
  return slots
    .filter((s) => nature(s) === 'ruolo')
    .reduce(
      ([x, y], s) => {
        const r = range(s.v.quantita) ?? [1, 1];
        return [x + r[0], y + r[1]];
      },
      [0, 0],
    );
}
function people(slots: Slot[]): string | null {
  const [a, b] = headcount(slots);
  if (!b) return null;
  return a === b ? `${a}` : `${a}-${b}`;
}
const persone = (n: string) => `${n} ${n === '1' ? 'persona' : 'persone'}`;

function label(s: Slot): string {
  const n = nature(s);
  const r = range(s.v.quantita);
  if (n === 'esterno') return 'di solito uno studio esterno';
  if (n === 'incarico')
    return r && r[1] > 1
      ? `incarico per ${persone(s.v.quantita!.replace(/\s*person[ae]$/i, ''))}`
      : 'incarico di chi ha già un ruolo';
  if (r) return persone(r[0] === r[1] ? `${r[0]}` : `${r[0]}-${r[1]}`);
  return s.v.quantita ?? 'posto di lavoro';
}

const uniq = <T,>(xs: T[]) => [...new Set(xs)];

/** Il nome di chi ricopre la carica si legge già nel nome della voce: non si ripete sotto. */
const named = (nome: string, chi: string) =>
  norm(chi)
    .split(/[\s,]+/)
    .filter((w) => w.length > 2)
    .every((w) => norm(nome).includes(w));

/** Tutti i ruoli del progetto, dall'impresa alle attività di ogni sede. */
function slotsOf(d: IdealDesignDto): Slot[] {
  const out: Slot[] = [];
  const add = (v: IdealItemDto, sede: string | null, luogo: string | null, att: string | null) =>
    out.push({ key: `${out.length}`, v, sede, luogo, att });
  for (const v of d.impresa?.voci ?? []) add(v, null, null, null);
  for (const s of d.sedi) {
    for (const v of s.sede_in_se?.voci ?? []) add(v, s.sede, s.luogo, null);
    for (const a of s.attivita) for (const v of a.voci) add(v, s.sede, s.luogo, a.attivita);
  }
  return out;
}

/** A chi risponde un ruolo dentro un insieme: lo stesso nome, preferendo la stessa sede, poi l'impresa. */
function parentIn(list: Slot[], s: Slot): Slot | null {
  if (!s.v.riporta_a) return null;
  const name = norm(s.v.riporta_a);
  const hits = list.filter((x) => x !== s && norm(x.v.nome) === name);
  return hits.find((x) => x.sede === s.sede) ?? hits.find((x) => !x.sede) ?? hits[0] ?? null;
}

/** In ordine di prima comparsa. */
function groupBy<T>(xs: T[], key: (x: T) => string): Array<[string, T[]]> {
  const m = new Map<string, T[]>();
  for (const x of xs) m.set(key(x), [...(m.get(key(x)) ?? []), x]);
  return [...m];
}

/** Chi ricopre una carica in essere, da dove si sa e con quali poteri. */
export interface Holder {
  chi: string;
  fonte: string;
  poteri: string | null;
}

export function OrgIdeal({
  design,
  company,
  addetti,
  holder,
  mandates,
  done,
  updating,
  error,
  onRetry,
  onTalk,
  onReview,
}: {
  design: IdealDesignDto;
  company: string;
  /** Gli addetti di tutta l'impresa, con la data, da confrontare con le persone che servono. */
  addetti: { n: number; al: string | null } | null;
  /** Le cariche in essere che nessuna voce rappresenta, come i rapporti con terzi (ADR 0101). */
  mandates: Array<{ carica: string; chi: string; fonte: string }>;
  /** Chi ricopre le cariche che la voce rappresenta, con la fonte e i poteri (ADR 0051): non persone da assegnare. */
  holder: (v: IdealItemDto) => Holder | null;
  /** Passo fatto: in testa la fascia «progettata» con «Rivedi il passo». */
  done: boolean;
  updating: boolean;
  error: string | null;
  onRetry: (() => void) | null;
  onTalk: ((text: string) => void) | null;
  onReview: (() => void) | null;
}) {
  const [sel, setSel] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  // Lo zoom: le funzioni aperte nella vista d'insieme; la sede guardata (null = tutta l'impresa).
  const [zoom, setZoom] = useState<Set<string>>(new Set());
  const [view, setView] = useState<string | null>(null);
  const slots = slotsOf(design);
  const organs = slots.filter((s) => nature(s) === 'organo');
  const roles = slots.filter((s) => nature(s) !== 'organo');
  const staff = roles.filter((s) => s.v.staff);
  const allLine = roles.filter((s) => !s.v.staff);
  const fns = groupBy(allLine, (s) => s.v.gruppo ?? 'Altri ruoli');
  // Un incarico di linea sta come etichetta sul ruolo di chi lo esercita (il ruolo a cui riporta): l'organigramma resta
  // fatto di ruoli. Un incarico sotto un organo, sotto un altro incarico o senza capo resta un riquadro.
  const bearer = (s: Slot): Slot | null => {
    if (nature(s) !== 'incarico') return null;
    const p = parentIn(allLine, s);
    return p && nature(p) === 'ruolo' ? p : null;
  };
  const line = allLine.filter((s) => !bearer(s));
  // Chi risponde a chi nella linea. Dalla cima si scende finché un solo ruolo guida tutti gli altri (chi guida
  // l'azienda): quelli stanno al centro, sotto gli organi. Dove la linea si divide cominciano le aree, affiancate, ognuna
  // con il suo responsabile e tutto ciò che gli risponde, di qualunque sede.
  const boss = (s: Slot) => parentIn(line, s);
  const under = (p: Slot) => line.filter((x) => x !== p && boss(x) === p);
  const spine: Slot[] = [];
  let level = line.filter((s) => !boss(s));
  while (level.length === 1 && !spine.includes(level[0]!) && under(level[0]!).length > 0) {
    spine.push(level[0]!);
    level = under(level[0]!).filter((x) => !spine.includes(x));
  }
  const reached = new Set<Slot>(spine);
  const reach = (s: Slot): Slot[] => {
    if (reached.has(s)) return [];
    reached.add(s);
    return [s, ...under(s).flatMap(reach)];
  };
  const areas = groupBy(level, (s) => s.v.gruppo ?? s.v.nome).map(
    ([name, heads]) => [name, heads.flatMap(reach)] as [string, Slot[]],
  );
  // Ciò che non si raggiunge dalla cima (un capo che non c'è, un giro) va nell'area della sua funzione.
  for (const s of line) {
    if (reached.has(s)) continue;
    const name = s.v.gruppo ?? 'Altri ruoli';
    const area = areas.find(([n]) => n === name);
    if (area) area[1].push(...reach(s));
    else areas.push([name, reach(s)]);
  }
  // Lo stesso ruolo (stessa natura, funzione e nome) in più sedi si legge una volta, con le sedi accanto.
  const twinKey = (s: Slot) => `${nature(s)}|${s.v.gruppo ?? ''}|${norm(s.v.nome)}`;
  const twins = new Map(groupBy(roles, twinKey));
  const twinsOf = (s: Slot) => (nature(s) === 'organo' ? [s] : (twins.get(twinKey(s)) ?? [s]));
  const holders = (all: Slot[]): Holder | null => {
    const hs = all.map((s) => holder(s.v)).filter((h): h is Holder => !!h);
    if (!hs.length) return null;
    return {
      chi: uniq(hs.map((h) => h.chi)).join(', '),
      fonte: uniq(hs.map((h) => h.fonte)).join(', '),
      poteri: uniq(hs.map((h) => h.poteri).filter(Boolean)).join(' · ') || null,
    };
  };
  const sites = design.sedi.filter((s) => s.sede_in_se || s.attivita.length).length;
  const picked = slots.find((s) => s.key === sel) ?? null;
  const pick = (s: Slot) => {
    setSel(sel === s.key ? null : s.key);
    if (sel !== s.key)
      setTimeout(() =>
        document.getElementById('ruolo')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      );
  };

  // only: il ruolo di una sola sede (la vista della sede), senza le sue copie nelle altre.
  const box = (s: Slot, tag = true, only = false) => {
    const n = nature(s);
    const all = only ? [s] : twinsOf(s);
    const tags = groupBy(
      allLine.filter((x) => {
        const b = bearer(x);
        return !!b && all.includes(b);
      }),
      twinKey,
    ).map(([, xs]) => xs);
    const places = tag ? uniq(all.map(where)).filter((x): x is string => !!x) : [];
    const p = n === 'ruolo' ? people(all) : null;
    const sub = p
      ? persone(p)
      : n === 'incarico' && all.length === 1 && (range(s.v.quantita)?.[1] ?? 1) > 1
        ? label(s)
        : null;
    const chi = holders(all);
    // In piccolo, sotto il nome dell'azienda, la professione standard e il livello (ADR 0131).
    const std = n === 'organo' || n === 'esterno' ? null : standardLine(s.v);
    const style =
      n === 'esterno'
        ? 'border-violet-200 bg-violet-50/60'
        : n === 'incarico'
          ? 'border-dashed border-black/25 bg-[#fbfbf8]'
          : 'border-emerald-300/80 bg-white';
    return (
      <div
        className={`rounded-xl border-[1.5px] px-2.5 py-1.5 transition hover:ring-[3px] hover:ring-accent/15 ${style} ${sel === s.key ? 'border-accent ring-[3px] ring-accent/20' : ''}`}
      >
        <button
          onClick={() => pick(s)}
          aria-expanded={sel === s.key}
          className="block w-full text-left"
        >
          <span className="block text-[13.5px] font-semibold leading-snug">
            {s.v.nome}
            <ItemBase v={s.v} />
            {places.slice(0, 3).map((x) => (
              <Badge key={x} tone="where">
                {x}
              </Badge>
            ))}
            {places.length > 3 && <Badge tone="where">{`+${places.length - 3}`}</Badge>}
          </span>
          {std && <span className="block text-[11px] leading-snug text-ink/45">{std}</span>}
          {sub && <span className="block text-xs text-ink/55">{sub}</span>}
          {chi && !named(s.v.nome, chi.chi) && (
            <span className="block text-xs text-ink/70">{chi.chi}</span>
          )}
        </button>
        {tags.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {tags.map((xs) => {
              const c = xs[0]!;
              const who = holders(xs);
              return (
                <button
                  key={c.key}
                  onClick={() => pick(c)}
                  aria-expanded={sel === c.key}
                  title="Incarico di chi ha questo ruolo: tocca per vederlo"
                  className={`rounded-md border border-dashed px-1.5 py-0.5 text-left text-[11px] leading-snug hover:border-accent ${sel === c.key ? 'border-accent text-accent' : 'border-black/25 text-ink/70'}`}
                >
                  <span className="font-medium">{c.v.nome}</span>
                  {c.v.obbligatorio && <span className="text-violet-700"> · per legge</span>}
                  {who && !named(c.v.nome, who.chi) && (
                    <span className="text-ink/50"> · {who.chi}</span>
                  )}
                </button>
              );
            })}
          </span>
        )}
      </div>
    );
  };

  // Chi risponde a chi dentro una funzione; un giro o un capo in un'altra funzione fa partire un ramo nuovo.
  // La sede sul ruolo solo se la funzione lavora in più posti.
  const tree = (list: Slot[], tag: boolean, only = false): ReactNode => {
    const seen = new Set<Slot>();
    const tw = only ? (s: Slot) => [s] : twinsOf;
    // Un riquadro per ruolo: il primo delle sue sedi lo rappresenta, e risponde a chi risponde il primo che ha un capo.
    const rep = (s: Slot) => tw(s).find((x) => list.includes(x)) ?? s;
    const reps = list.filter((s) => rep(s) === s);
    const up = (r: Slot) => {
      for (const t of tw(r).filter((x) => list.includes(x))) {
        const p = parentIn(list, t);
        if (p && rep(p) !== r) return rep(p);
      }
      return null;
    };
    const kids = (p: Slot) => reps.filter((x) => up(x) === p);
    const li = (s: Slot): ReactNode => {
      if (seen.has(s)) return null;
      seen.add(s);
      const ks = kids(s).filter((k) => !seen.has(k));
      return (
        <li key={s.key} className={TICK}>
          {box(s, tag, only)}
          {ks.length > 0 && <ul className={BRANCH}>{ks.map(li)}</ul>}
        </li>
      );
    };
    const roots = reps.filter((s) => !up(s));
    const rest = () => reps.filter((s) => !seen.has(s));
    const first = roots.map(li);
    return (
      <ul className={BRANCH}>
        {first}
        {rest().map(li)}
      </ul>
    );
  };

  // Gli organi in cima, livello per livello (chi risponde a un altro organo scende di un livello).
  const depth = (s: Slot, seen = new Set<Slot>()): number => {
    const p = parentIn(organs, s);
    if (!p || seen.has(p)) return 0;
    seen.add(s);
    return 1 + depth(p, seen);
  };
  const levels = groupBy(organs, (s) => String(depth(s))).sort(([a], [b]) => Number(a) - Number(b));
  // Le persone che servono accanto a quelle che ci lavorano: se il progetto se ne allontana molto, lo si dice.
  const [lo, hi] = headcount(roles);
  const gap =
    !!addetti && addetti.n > 0 && hi > 0 && (addetti.n > hi * 1.25 || addetti.n < lo * 0.75);
  const sedi = design.sedi.filter((x) => roles.some((r) => r.sede === x.sede));
  const site = view ? (sedi.find((x) => x.sede === view) ?? null) : null;
  // Le funzioni chiuse finché non si aprono, se sono tante; con pochi ruoli tutto aperto.
  const small = line.length <= 8;
  const zoomed = (name: string) => small || zoom.has(name);
  const areaHead = (name: string, list: Slot[], places: Array<string | null>, toggle: boolean) => {
    const p = people(list);
    const body = (
      <>
        <p className="text-[13.5px] font-semibold leading-snug">
          {toggle && !small && (
            <span
              className={`mr-1 inline-block text-ink/40 transition-transform ${zoomed(name) ? 'rotate-90' : ''}`}
              aria-hidden
            >
              ▸
            </span>
          )}
          {name}
        </p>
        <p className="text-xs text-ink/55">
          {p ? persone(p) : `${list.length} ${list.length === 1 ? 'ruolo' : 'ruoli'}`}
          {places.length === 1 && places[0] ? ` · ${places[0]}` : ''}
          {toggle &&
            !small &&
            !zoomed(name) &&
            list.length > 0 &&
            ` · ${list.length} ${list.length === 1 ? 'ruolo' : 'ruoli'}`}
        </p>
      </>
    );
    const cls =
      'block w-full rounded-xl border-[1.5px] border-[#a9bcf5] bg-[#f3f6ff] px-2.5 py-1.5 text-left sm:text-center';
    if (!toggle || small) return <div className={cls}>{body}</div>;
    return (
      <button
        aria-expanded={zoomed(name)}
        className={`${cls} transition hover:ring-[3px] hover:ring-accent/15`}
        onClick={() => {
          const n = new Set(zoom);
          if (n.has(name)) n.delete(name);
          else n.add(name);
          setZoom(n);
        }}
      >
        {body}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {done && (
        <DoneBand
          title="La tua azienda è progettata"
          text="Come dovrebbe essere, a norma ed efficiente. Tocca un ruolo per vederlo."
          onReview={onReview}
        />
      )}
      <div>
        {slots.length > 0 && (
          <>
            <p className="text-[15px] leading-relaxed text-ink/80">
              <b className="font-semibold text-ink">{company}</b>: {fns.length}{' '}
              {fns.length === 1 ? 'funzione' : 'funzioni'}
              {sites > 0 &&
                `, ${sites} ${sites === 1 ? 'sede dove si lavora' : 'sedi dove si lavora'}`}
              , {roles.length} ruoli. L’ho progettata da visura, sedi, attività e impianti, come la
              organizzerebbe un buon consulente per un’azienda come la tua, nel rispetto della
              legge.
              {updating && (
                <span className="text-accent"> La completo con le ultime risposte…</span>
              )}
            </p>
            <Glance
              tiles={[
                !!people(roles) && [people(roles)!, 'persone che servono'],
                !!addetti && [
                  addetti.n,
                  `ci lavorano oggi${addetti.al ? ` (al ${addetti.al})` : ''}`,
                ],
                [roles.filter((s) => nature(s) === 'ruolo').length, 'posti di lavoro'],
                [roles.filter((s) => nature(s) === 'incarico').length, 'incarichi da affidare'],
                [roles.filter((s) => nature(s) === 'esterno').length, 'studi esterni'],
                [roles.filter((s) => s.v.obbligatorio).length, 'richiesti dalla legge'],
              ]}
            />
            {gap && (
              <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
                Il progetto prevede {people(roles)} persone, oggi ce ne lavorano {addetti!.n}.{' '}
                {/* Il perché lo scrive il cervello (ADR 0131); se manca, la pagina lo dice. */}
                {design.persone_perche
                  ? design.persone_perche
                  : addetti!.n > hi
                    ? 'Il perché non è scritto: forse manca qualche ruolo.'
                    : 'Il perché non è scritto: forse alcuni ruoli si possono unire.'}{' '}
                {onTalk && (
                  <button
                    className="font-medium text-accent hover:underline"
                    onClick={() =>
                      onTalk(
                        `Nel progetto servono ${people(roles)} persone, ma oggi ne lavorano ${addetti!.n}: `,
                      )
                    }
                  >
                    Dimmi cosa non torna
                  </button>
                )}
              </p>
            )}
          </>
        )}
        {error && (
          <p className="mt-2 text-sm text-red-700">
            Una parte del progetto non è riuscita: {ownerError(error)}{' '}
            {onRetry && (
              <button className="text-accent hover:underline" onClick={onRetry}>
                Riprova
              </button>
            )}
          </p>
        )}
      </div>

      <section className="space-y-2 pt-2">
        <div className="px-1">
          <h3 className="text-base font-semibold">Organigramma</h3>
          <p className="text-xs text-ink/55">Chi risponde a chi in tutta l’impresa.</p>
        </div>
        <Legend />
        {sedi.length > 1 && (
          <div className="flex flex-wrap gap-1.5 px-1" role="tablist" aria-label="Vista">
            {[null, ...sedi.map((x) => x.sede)].map((k) => (
              <button
                key={k ?? ''}
                role="tab"
                aria-selected={view === k}
                onClick={() => {
                  setView(k);
                  setSel(null);
                }}
                className={`rounded-full px-3 py-1 text-xs font-medium ring-1 transition ${view === k ? 'bg-ink text-white ring-ink' : 'bg-white text-ink/70 ring-black/10 hover:ring-black/30'}`}
              >
                {k === null
                  ? 'Tutta l’impresa'
                  : (sedi.find((x) => x.sede === k)?.luogo?.split(',')[0] ?? k)}
              </button>
            ))}
          </div>
        )}
        <div className="rounded-3xl bg-white px-4 py-5 shadow-sm ring-1 ring-black/5">
          {site ? (
            <SiteView
              name={site.sede}
              luogo={site.luogo}
              need={people(roles.filter((r) => r.sede === site.sede))}
              have={site.addetti}
              up={uniq(
                line
                  .filter((r) => r.sede === site.sede)
                  .map((r) => parentIn(line, r))
                  .filter((p): p is Slot => !!p && p.sede !== site.sede)
                  .map((p) => p.v.nome),
              )}
              areas={groupBy(
                line.filter((r) => r.sede === site.sede),
                (r) => r.v.gruppo ?? 'Altri ruoli',
              ).map(([name, list]) => (
                <div key={name}>
                  {areaHead(name, list, [], false)}
                  {tree(list, false, true)}
                </div>
              ))}
              staff={staff
                .filter((r) => r.sede === site.sede)
                .map((r) => (
                  <div key={r.key}>{box(r, false, true)}</div>
                ))}
            />
          ) : (
            <>
              {!small && areas.length > 1 && (
                <p className="-mt-1 mb-2 text-right text-xs">
                  <button
                    className="text-accent hover:underline"
                    onClick={() =>
                      setZoom(
                        zoom.size >= areas.length ? new Set() : new Set(areas.map(([n]) => n)),
                      )
                    }
                  >
                    {zoom.size >= areas.length ? 'Chiudi le funzioni' : 'Apri tutte le funzioni'}
                  </button>
                </p>
              )}
              {levels.length ? (
                levels.map(([k, list]) => (
                  <div key={k}>
                    <div className="flex flex-wrap justify-center gap-2">
                      {list.map((s) => (
                        <div key={s.key} className="w-full sm:w-64">
                          <Organ
                            s={s}
                            chi={holder(s.v)}
                            onPick={() => pick(s)}
                            on={sel === s.key}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mx-auto h-4 w-0 border-l-[1.5px] border-black/20" />
                  </div>
                ))
              ) : (
                <div className="mx-auto h-0" />
              )}
              {spine.map((s) => (
                <div key={s.key}>
                  <div className="mx-auto w-full sm:w-64">{box(s)}</div>
                  <div className="mx-auto h-4 w-0 border-l-[1.5px] border-black/20" />
                </div>
              ))}
              {areas.length > 0 && (
                <div className="sm:overflow-x-auto">
                  <div
                    className="relative ml-3 space-y-3 border-l-[1.5px] border-black/20 pl-3 sm:ml-0 sm:grid sm:space-y-0 sm:border-l-0 sm:pl-0 sm:pt-4"
                    style={{ gridTemplateColumns: `repeat(${areas.length}, minmax(11rem, 1fr))` }}
                  >
                    {areas.length > 1 && (
                      <span
                        aria-hidden
                        className="absolute top-0 hidden border-t-[1.5px] border-black/20 sm:block"
                        style={{ left: `${50 / areas.length}%`, right: `${50 / areas.length}%` }}
                      />
                    )}
                    {areas.map(([name, list]) => {
                      const places = [...new Set(list.map(where))];
                      return (
                        <div
                          key={name}
                          className="relative before:absolute before:-left-3 before:top-4 before:w-3 before:border-t-[1.5px] before:border-black/20 before:content-[''] sm:px-1 sm:before:-top-4 sm:before:left-1/2 sm:before:h-4 sm:before:w-0 sm:before:border-l-[1.5px] sm:before:border-t-0"
                        >
                          {areaHead(name, list, places, true)}
                          {zoomed(name) && tree(list, places.length > 1)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {staff.length > 0 && (
                <div className="mt-5 space-y-3 border-t border-dashed border-black/10 pt-3">
                  {groupBy(staff, (s) => s.v.riporta_a ?? 'chi guida l’impresa').map(
                    ([to, list]) => (
                      <div key={to}>
                        <p className="mb-2 text-xs font-semibold">
                          In staff a {to}{' '}
                          <span className="font-normal text-ink/50">
                            · fuori dalla linea di comando
                          </span>
                        </p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]">
                          {list
                            .filter((s) => twinsOf(s)[0] === s)
                            .map((s) => (
                              <div key={s.key}>{box(s)}</div>
                            ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </>
          )}
          {!slots.length && (
            <p className="py-4 text-center text-sm text-ink/50">
              {updating ? (
                'Sto progettando la tua organizzazione…'
              ) : (
                <>
                  Il progetto non c’è ancora.{' '}
                  {onRetry && !error && (
                    <button className="text-accent hover:underline" onClick={onRetry}>
                      Progettala ora
                    </button>
                  )}
                </>
              )}
            </p>
          )}
        </div>
        {mandates.length > 0 && !view && (
          <div className="rounded-2xl bg-white px-4 py-3 ring-1 ring-black/5">
            <p className="text-xs font-semibold">
              Cariche e mandati{' '}
              <span className="font-normal text-ink/50">
                · fuori dall’organigramma: non sono ruoli dell’azienda
              </span>
            </p>
            <ul className="mt-1.5 space-y-0.5 text-sm">
              {mandates.map((m, i) => (
                <li key={i}>
                  <span className="font-medium">{m.carica}</span>{' '}
                  <span className="text-ink/70">· {m.chi}</span>{' '}
                  <span className="text-xs text-ink/40">{m.fonte}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {picked && (
          <Detail
            s={picked}
            all={twinsOf(picked)}
            chi={holders(twinsOf(picked))}
            onClose={() => setSel(null)}
            onTalk={onTalk}
            others={slots.map((x) => ({ nome: x.v.nome, codice: x.v.professione?.codice ?? null }))}
            fallbackPlace={slots.find((x) => x.luogo)?.luogo ?? null}
          />
        )}
      </section>

      {roles.length > 0 && (
        <section className="space-y-2 pt-2">
          <div className="px-1">
            <h3 className="text-base font-semibold">Funzionigramma</h3>
            <p className="text-xs text-ink/55">
              Cosa fa ogni funzione, con quali ruoli e quante persone.
            </p>
          </div>
          <ul className="divide-y divide-black/5 rounded-3xl bg-white shadow-sm ring-1 ring-black/5">
            {groupBy(roles, (s) => s.v.gruppo ?? 'Altri ruoli').map(([name, list]) => {
              const isOpen = open.has(name);
              const places = [...new Set(list.map(where))];
              const duties = [...new Set(list.flatMap((s) => s.v.dettagli.slice(0, 1)))].slice(
                0,
                4,
              );
              const p = people(list);
              return (
                <li key={name}>
                  <button
                    aria-expanded={isOpen}
                    onClick={() => {
                      const n = new Set(open);
                      if (n.has(name)) n.delete(name);
                      else n.add(name);
                      setOpen(n);
                    }}
                    className="grid w-full grid-cols-[1rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-black/[0.015] sm:grid-cols-[1rem_11rem_minmax(0,1fr)_7rem]"
                  >
                    <span
                      className={`text-ink/40 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                      aria-hidden
                    >
                      ▸
                    </span>
                    <span className="font-semibold">
                      {name}
                      <span className="block text-xs font-normal text-ink/50">
                        {places.filter(Boolean).join(', ') || 'tutta l’impresa'}
                      </span>
                    </span>
                    <span className="col-start-2 text-sm text-ink/75 sm:col-start-auto">
                      {duties.join(' · ')}
                    </span>
                    <span className="col-start-2 text-xs text-ink/55 sm:col-start-auto sm:text-right">
                      {list.length} {list.length === 1 ? 'ruolo' : 'ruoli'}
                      {p && <span className="block font-medium text-accent">{persone(p)}</span>}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-3 pl-11">
                      {list
                        .filter((s) => twinsOf(s)[0] === s)
                        .map((s) => (
                          <div
                            key={s.key}
                            className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 border-t border-dashed border-black/5 py-2 first:border-t-0"
                          >
                            <span className="text-sm font-medium">
                              {s.v.nome}
                              <ItemBase v={s.v} />
                              {uniq(twinsOf(s).map(where))
                                .filter(Boolean)
                                .map((x) => (
                                  <Badge key={x} tone="where">
                                    {x}
                                  </Badge>
                                ))}
                            </span>
                            <span className="text-xs text-ink/55">
                              {nature(s) === 'ruolo' && twinsOf(s).length > 1 && people(twinsOf(s))
                                ? persone(people(twinsOf(s))!)
                                : label(s)}{' '}
                              ·{' '}
                              <button
                                className="font-medium text-accent hover:underline"
                                onClick={() => pick(s)}
                              >
                                Vedi
                              </button>
                            </span>
                            <span className="col-span-2 text-xs text-ink/55">
                              {s.v.dettagli.join(' · ')}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <Feeds steps={['La mappa dell’azienda', 'Procedure', 'Documenti e collegamenti']} />
    </div>
  );
}

const BRANCH = 'ml-2.5 mt-1.5 space-y-1.5 border-l-[1.5px] border-black/20 pl-2.5';
const TICK =
  "relative before:absolute before:-left-2.5 before:top-4 before:w-2.5 before:border-t-[1.5px] before:border-black/20 before:content-['']";

function Badge({ tone, children }: { tone: 'law' | 'where'; children: ReactNode }) {
  return (
    <span
      className={`ml-1 inline-block whitespace-nowrap rounded-md px-1.5 align-[1px] text-[10.5px] font-semibold ${tone === 'law' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}
    >
      {children}
    </span>
  );
}

function Legend() {
  const sw = 'inline-block h-3 w-[18px] rounded align-[-1px] mr-1.5 border-[1.5px]';
  return (
    <p className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs text-ink/55">
      <span>
        <i className={`${sw} border-emerald-300 bg-white`} />
        posto di lavoro
      </span>
      <span>
        <i className={`${sw} border-dashed border-black/30 bg-[#fbfbf8]`} />
        incarico di chi ha già un ruolo
      </span>
      <span>
        <i className={`${sw} border-violet-200 bg-violet-50`} />
        di solito uno studio esterno
      </span>
      <span>
        <ItemBase v={{ base: 'legge' }} /> lo chiede la legge
      </span>
      <span>
        <ItemBase v={{ base: 'prassi' }} /> lo fanno le aziende come la tua
      </span>
      <span>
        <ItemBase v={{ base: 'crescere' }} /> per crescere
      </span>
    </p>
  );
}

/** La vista di una sede: chi ci lavora e quante persone servono, accanto a quelle che ci lavorano oggi. */
function SiteView({
  name,
  luogo,
  need,
  have,
  up,
  areas,
  staff,
}: {
  name: string;
  luogo: string | null;
  need: string | null;
  have: number | null;
  /** I ruoli di tutta l'impresa a cui rispondono i responsabili della sede. */
  up: string[];
  areas: ReactNode[];
  staff: ReactNode[];
}) {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <p className="text-base font-semibold">{name}</p>
        {luogo && <p className="text-xs text-ink/55">{luogo}</p>}
        <p className="mt-1 text-sm text-ink/75">
          {need ? `Servono ${persone(need)}` : 'Nessun posto di lavoro proprio'}
          {have != null && ` · ci lavorano oggi ${have}`}
        </p>
        {up.length > 0 && (
          <p className="text-xs text-ink/55">Risponde a {up.join(', ')}, per tutta l’impresa</p>
        )}
      </div>
      {areas.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-[repeat(auto-fill,minmax(14rem,1fr))]">{areas}</div>
      ) : (
        <p className="text-center text-sm text-ink/50">
          Questa sede lavora con i ruoli di tutta l’impresa.
        </p>
      )}
      {staff.length > 0 && (
        <div className="border-t border-dashed border-black/10 pt-3">
          <p className="mb-2 text-xs font-semibold">
            In staff <span className="font-normal text-ink/50">· fuori dalla linea di comando</span>
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]">
            {staff}
          </div>
        </div>
      )}
    </div>
  );
}

function Organ({
  s,
  chi,
  onPick,
  on,
}: {
  s: Slot;
  chi: Holder | null;
  onPick: () => void;
  on: boolean;
}) {
  return (
    <button
      onClick={onPick}
      aria-expanded={on}
      className={`block w-full rounded-xl border-[1.5px] border-[#a9bcf5] bg-[#f3f6ff] px-2.5 py-1.5 text-left transition hover:ring-[3px] hover:ring-accent/15 sm:text-center ${on ? 'border-accent ring-[3px] ring-accent/20' : ''}`}
    >
      <span className="block text-[13.5px] font-semibold leading-snug">
        {s.v.nome}
        <ItemBase v={s.v} />
      </span>
      <span className="block text-xs text-ink/70">
        {chi ? chi.chi : (s.v.quantita ?? 'organo')}
      </span>
    </button>
  );
}

function Detail({
  s,
  all,
  chi,
  onClose,
  onTalk,
  others,
  fallbackPlace,
}: {
  s: Slot;
  /** Lo stesso ruolo in tutte le sue sedi. */
  all: Slot[];
  chi: Holder | null;
  onClose: () => void;
  onTalk: ((text: string) => void) | null;
  /** Tutti i ruoli dell'organigramma con la professione, per i ruoli vicini (ADR 0133). */
  others: OtherRole[];
  /** Il luogo della prima sede, per i ruoli di tutta l'impresa. */
  fallbackPlace: string | null;
}) {
  const place =
    all.length > 1
      ? uniq(all.map((x) => x.sede ?? 'tutta l’impresa')).join(', ')
      : s.sede
        ? `${s.sede}${s.luogo ? ` · ${s.luogo}` : ''}`
        : 'tutta l’impresa';
  return (
    <div
      id="ruolo"
      className="rounded-2xl border-l-[3px] border-l-accent bg-white px-4 py-4 ring-1 ring-accent/25"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold">
            {s.v.nome}
            <ItemBase v={s.v} />
          </p>
          <p className="text-xs text-ink/55">
            {[s.v.gruppo, s.att, place, s.v.riporta_a && `risponde a ${s.v.riporta_a}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <button
          aria-label="Chiudi"
          className="px-1.5 text-xl leading-none text-ink/40 hover:text-ink"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      {s.v.descrizione && <p className="mt-2 text-sm">{s.v.descrizione}</p>}
      <div className="mt-3 grid gap-x-5 gap-y-3 sm:grid-cols-2">
        {s.v.dettagli.length > 0 && (
          <Fact k="Mansioni">
            <ul className="list-disc pl-4">
              {s.v.dettagli.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </Fact>
        )}
        <Fact k={nature(s) === 'organo' ? 'Chi lo forma' : 'Quante persone'}>
          {nature(s) === 'organo'
            ? chi
              ? `${chi.chi} (${chi.fonte})`
              : (s.v.quantita ?? '—')
            : all.length > 1 && nature(s) === 'ruolo' && people(all)
              ? `${persone(people(all)!)} in ${all.length} sedi`
              : label(s)}
        </Fact>
        {chi && nature(s) !== 'organo' && (
          <Fact k="Chi la ricopre già">{`${chi.chi} (${chi.fonte})`}</Fact>
        )}
        {chi?.poteri && (
          <Fact k={`I poteri (${chi.fonte})`}>
            <span className="line-clamp-6">{chi.poteri}</span>
          </Fact>
        )}
        {s.v.perche && <Fact k="Perché c’è nella tua azienda">{s.v.perche}</Fact>}
        {s.v.riferimento && (
          <Fact k="Riferimento">
            <ItemBase v={s.v} detail />
          </Fact>
        )}
        <LibrarySources refs={s.v.libreria} fondata={s.v.fondata} conti={s.v.conti} />
      </div>
      {/* Processi, procedure e programmi del ruolo (ADR 0137); gli strumenti con cui lavora stanno qui, con i prodotti. */}
      <RoleWork
        nome={s.v.nome}
        sedi={uniq(all.map((x) => x.sede))}
        v={{ ...s.v, fasi: uniq(all.flatMap((x) => x.v.fasi ?? [])), strumenti: all.flatMap((x) => x.v.strumenti ?? []) }}
        natura={nature(s)}
      />
      <RoleStandards
        v={s.v}
        others={others}
        regione={(s.luogo ?? fallbackPlace ?? '').split(',').map((x) => x.trim()).filter(Boolean).at(-1) ?? null}
        provincia={(s.luogo ?? fallbackPlace ?? '').split(',').map((x) => x.trim()).filter(Boolean).at(-2) ?? null}
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {onTalk && (
          <button
            className="rounded-xl bg-white px-3.5 py-2 text-sm font-medium text-accent ring-1 ring-accent/30 hover:ring-accent"
            onClick={() => onTalk(`Sul ruolo «${s.v.nome}»${s.sede ? ` (${s.sede})` : ''}: `)}
          >
            💬 Dimmi cosa cambiare
          </button>
        )}
        <span className="text-xs text-ink/40">
          In questa fase nessun ruolo si toglie: chi lo fa e cosa tenere si decide nella
          configurazione operativa.
        </span>
      </div>
    </div>
  );
}

function Fact({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-ink/40">{k}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}
